import { defineStyleConfig } from '@chakra-ui/react';
import { createMultiStyleConfigHelpers } from '@chakra-ui/styled-system';

import { themeVar } from './tenantTheme';

// Button
export const Button = defineStyleConfig({
  baseStyle: {
    // `radii.md` is NOT var-ified: it is shared by cards, inputs and modals, so re-pointing the
    // scale entry would reshape every `md`-radius surface in the app. Instead the Button config
    // states the radius it already inherited from the base theme's baseStyle (`md`) as its own
    // token, so a tenant moves buttons alone. The `unselected`/`underline` variants still set
    // `borderRadius: 0` and variants merge after baseStyle, so they keep winning.
    borderRadius: themeVar('button.borderRadius', 'var(--chakra-radii-md)'),
    fontWeight: themeVar('button.fontWeight', '500'),
    // Neither of these was declared before. Both are inherited properties, so the faithful
    // fallback is the value the button computes today, not a reset:
    //   - letter-spacing has no UA rule for <button>, so it inherits -> `inherit`.
    //   - text-transform IS set by Chakra's CSS reset (`:where(button, select) { none }`), so the
    //     computed value today is `none`, and `inherit` would be a behaviour change.
    letterSpacing: themeVar('button.letterSpacing', 'inherit'),
    textTransform: themeVar('button.textTransform', 'none'),
  },
  variants: {
    unselected: {
      borderBottomWidth: '2px',
      borderBottomColor: 'transparent',
      borderRadius: 0,
      fontSize: 'sm',
    },
    underline: {
      borderBottomWidth: '2px',
      // `colors.secondary` is a scale *object*, so it never lands in Chakra's `__cssMap` and this
      // literal passes through as the invalid colour `secondary` (the declaration is dropped and
      // the border falls back to currentColor). Keeping `secondary` as the `var()` fallback
      // preserves that byte-for-byte, while `button.underlineColor` makes it actually settable.
      borderBottomColor: themeVar('button.underlineColor', 'secondary'),
      borderRadius: 0,
      fontSize: 'sm',
    },
  },

  sizes: {
    lg: {
      h: '40px',
      px: 5,
    },
    xl: {
      h: '60px',
      px: 5,
    },
  },
});

const simpleFieldHelpers = createMultiStyleConfigHelpers(['form', 'label', 'input']);

export const SimpleField = simpleFieldHelpers.defineMultiStyleConfig({
  baseStyle: (props) => ({
    form: {
      bgColor: themeVar('field.bg', 'var(--chakra-colors-bg-surface)'),
      borderRadius: themeVar('field.borderRadius', 'var(--chakra-radii-md)'),
      // The `removePadding` branch stays a literal 0: `field.padding` is the padded case, and
      // `padding: 0` is what "remove the padding" means regardless of what a tenant picks.
      padding: props?.removePadding ? 0 : themeVar('field.padding', 'var(--chakra-space-4)'),
      transition: 'all 0.2s ease-in 0s',
      borderWidth: themeVar('field.borderWidth', '1px'),
      // The invalid branch stays on the `error` semantic token, which carries its own
      // `--peli-semantic-error` indirection.
      borderColor: props?.isInvalid ? 'error' : themeVar('field.borderColor', 'var(--chakra-colors-border-default)'),
      _hover: {
        bgColor: themeVar('field.bg', 'var(--chakra-colors-bg-surface)'),
        borderColor: themeVar('field.borderColorHover', 'var(--chakra-colors-border-hover)'),
      },
      _focus: {
        // borderColor: 'transparent',
        borderColor: themeVar('field.borderColorFocus', 'var(--chakra-colors-border-focused)'),
      },
      // Previously hardcoded in SimpleField's JSX, where no variant could reach it. The old code
      // spread the resolved `_focus` and then overrode borderColor unconditionally, so every
      // variant ended up with this same ring - keeping it on baseStyle preserves that exactly.
      //
      // This is the branch that actually fires in practice (the control lives inside the box), so
      // it shares `field.borderColorFocus` with `_focus` above - the schema has one key for "the
      // border colour while focused" and an override that only moved `_focus` would be invisible.
      // The static fallback stays `blue.500`, so nothing moves until a tenant sets it.
      _focusWithin: {
        borderColor: themeVar('field.borderColorFocus', 'var(--chakra-colors-blue-500)'),
        shadow: 'focusRing',
      },
      _invalid: {
        borderColor: 'error',
      },
    },
    // The control itself. These values lived as inline props on SimpleInput's <Input>, where they
    // beat anything the theme could say; they belong here so variants (and tenants) can override.
    input: {
      fontSize: themeVar('input.fontSize', 'var(--chakra-fontSizes-md)'),
      borderRadius: themeVar('input.borderRadius', '0'),
      _placeholder: {
        color: themeVar('input.placeholderColor', 'var(--chakra-colors-text-placeholder)'),
      },
    },
  }),
  variants: {
    inline: {
      form: {
        background: 'transparent',
        p: '0',
        _hover: {
          background: 'unset',
        },
      },
    },
    outline: (props) => ({
      form: {
        borderRadius: themeVar('field.borderRadius', '6px'),
        background: 'bg.surface',
        borderWidth: '1px',
        borderColor: props?.isInvalid ? 'error' : 'border.default',
        _hover: {
          borderColor: 'border.hover',
        },
        _focus: {
          borderColor: 'border.focused',
        },
        _invalid: {
          borderColor: 'error',
        },
      },
    }),
    unstyled: {
      form: {
        p: '0',
        borderWidth: '0',
        background: 'transparent',
        _hover: {
          background: 'transparent',
        },
        _focus: {
          borderColor: 'transparent',
        },
      },
    },
    ghost: {
      form: {
        p: '0',
        borderWidth: '0',
        background: 'transparent',
        boxShadow: 'none',
        _hover: {
          background: 'transparent',
          borderColor: 'transparent',
        },
        _focus: {
          borderColor: 'transparent',
        },
        _focusWithin: {
          borderColor: 'transparent',
          boxShadow: 'none',
        },
      },
    },
    mini: (props) => ({
      form: {
        px: '10px',
        pt: '5px',
        pb: '0px',
        h: 'full',
        fontSize: 'sm',
        size: 'sm',
        borderRadius: themeVar('field.borderRadius', '3px'),
        background: 'bg.surface',
        borderWidth: '1px',
        borderColor: props?.isInvalid ? 'error' : 'border.default',
        _hover: {
          borderColor: 'border.hover',
        },
        _focus: {
          borderColor: 'border.focused',
        },
        _invalid: {
          borderColor: 'error',
        },
      },
    }),
  },
});

const radioHelpers = createMultiStyleConfigHelpers(['container', 'button']);

// The selected pill. Spelled once and spread, because the same declaration block is repeated for
// the base, `_first` and `_last` cases in every variant below - six copies of the same
// `gray.800`/`white` pair before this, which is exactly how they drifted out of reach of the theme.
const selectedPill = (radius: string | Record<string, string>) => ({
  bg: themeVar('switch.selectedBg', 'var(--chakra-colors-gray-800)'),
  color: themeVar('switch.selectedColor', 'white'),
  ...(typeof radius === 'string' ? { borderRadius: radius } : radius),
});

const PILL_RADIUS = themeVar('switch.borderRadius', 'var(--chakra-radii-md)');

export const CustomRadio = radioHelpers.defineMultiStyleConfig({
  baseStyle: (props) => ({
    container: {
      color: themeVar('switch.color', 'black'),
      borderRadius: PILL_RADIUS,
      '& > input:checked ~ div': selectedPill(PILL_RADIUS),
      _first: {
        '& > input:checked ~ div': selectedPill(PILL_RADIUS),
        borderRadius: PILL_RADIUS,
      },
      _last: {
        '& > input:checked ~ div': selectedPill(PILL_RADIUS),
        borderRadius: PILL_RADIUS,
      },
    },
    button: {
      cursor: 'pointer',
      px: 4,
      _checked: {
        color: 'white',
        fontWeight: '900',
      },
      alignItems: 'center',
      fontSize: 'lg',
      width: '100%',
      justifyContent: props?.isVertical ? 'flex-start' : 'space-around',
    },
  }),
  variants: {
    // `optionBg` carries a different fallback in each variant, preserving the two looks exactly
    // while giving a tenant one key for "the colour of an unselected option".
    inline: {
      container: {
        bg: themeVar('switch.optionBg', 'white'),
        color: themeVar('switch.color', 'black'),
        _first: {
          '& > input:checked ~ div': selectedPill({ borderLeftRadius: PILL_RADIUS }),
          borderLeftRadius: PILL_RADIUS,
        },
        _last: {
          '& > input:checked ~ div': selectedPill({ borderRightRadius: PILL_RADIUS }),
          borderRightRadius: PILL_RADIUS,
        },
      },
    },
    outline: {
      container: {
        bg: themeVar('switch.optionBg', 'var(--chakra-colors-gray-200)'),
        color: themeVar('switch.color', 'black'),
        _first: {
          '& > input:checked ~ div': selectedPill({ borderLeftRadius: PILL_RADIUS }),
          borderLeftRadius: PILL_RADIUS,
        },
        _last: {
          // The duplicate `bg` was already here, restating the container colour on the last
          // option; kept so the variant renders identically.
          bg: themeVar('switch.optionBg', 'var(--chakra-colors-gray-200)'),
          '& > input:checked ~ div': selectedPill({ borderRightRadius: PILL_RADIUS }),
          borderRightRadius: PILL_RADIUS,
        },
      },
    },
  },
});

const tooltipHelpers = createMultiStyleConfigHelpers(['icon', 'content']);

export const SimpleTooltip = tooltipHelpers.defineMultiStyleConfig({
  baseStyle: {
    icon: {
      position: 'absolute',
      right: '0.25em',
      top: '0.25em',
    },
    content: {
      fontSize: 'md',
      bg: 'gray.800',
      color: 'white',
      padding: '0.25rem',
    },
  },
  variants: {
    inline: {
      icon: {
        position: 'unset',
        marginLeft: '0.5rem',
      },
    },
  },
});

const tableHelpers = createMultiStyleConfigHelpers(['table', 'thead', 'tbody', 'tr', 'th', 'td']);

export const Table = tableHelpers.defineMultiStyleConfig({
  baseStyle: {
    table: {},
    th: {},
    td: {
      whiteSpace: 'nowrap',
    },
  },
});
